import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/store/authStore'
import { useTenantStore } from '@/store/tenantStore'
import {
  BookOpen,
  Gamepad2,
  Home,
  Lock,
  Map,
  UserRound,
} from 'lucide-react'
import { toast } from 'sonner'
import { useTodayAttendance } from '@/hooks/learner/useTodayAttendance'
import { cn } from '@/lib/utils'
import { LanguageSwitcher } from '@/components/common/LanguageSwitcher'
import { getTenantLogoUrl, getTenantId } from '@/utils/tenant'
import { HeaderLogoutButton } from '@/components/layout/HeaderLogoutButton'
import { CourseJourneyDialog } from '@/components/learner/CourseJourneyDialog'
import { UserInitialAvatar } from '@/components/learner/ui/UserInitialAvatar'
import { LearnerChromeProvider } from '@/components/layout/LearnerChrome'

export default function LearnerLayout() {
  const { pathname } = useLocation()
  const { user } = useAuthStore()
  const { tenantInfo } = useTenantStore()
  const { t } = useTranslation('nav')
  const { data: attendance } = useTodayAttendance()
  const [journeyOpen, setJourneyOpen] = useState(false)
  const [isChromeHidden, setChromeHidden] = useState(false)
  const contentRef = useRef<HTMLElement>(null)

  useLayoutEffect(() => {
    if (contentRef.current) contentRef.current.scrollTop = 0
  }, [pathname])
  // 축3b 프리뷰 + 축5C 소프트 게이팅: 대기/휴원/퇴원 원생은 학습 탭 잠금 (라우트 차단은 usePreviewGate — 이중 방어)
  const isPreview = !!attendance && attendance.enrollmentStatus !== 'ENROLLED'
  const isAttendanceRestricted =
    attendance?.enrollmentStatus === 'ENROLLED' && attendance.attendanceStatus !== 'IN'
  const isTrialRestricted = attendance?.trialStatus === 'PENDING' || attendance?.trialStatus === 'ACTIVE'
  const contentFeeGate = attendance?.contentFeeGate
  const isJourneyLocked = isPreview || isTrialRestricted || !!contentFeeGate
  const journeyLockedToastKey = isTrialRestricted
    ? 'trial.lockedToast'
    : contentFeeGate
      ? `preview.feeGate.${contentFeeGate}`
      : 'preview.lockedToast'
  const lockedTabs = ['/my/today', '/my/library', '/my/playground']

  const tabs = useMemo(
    () => [
      {
        to: '/my/today',
        icon: Home,
        label: t('learnerTabs.today'),
      },
      {
        to: '/my/library',
        label: t('learnerTabs.library'),
        icon: BookOpen,
      },
      {
        to: '/my/playground',
        icon: Gamepad2,
        label: t('learnerTabs.playground'),
      },
      {
        to: '/my/profile',
        icon: UserRound,
        label: t('learnerTabs.profile'),
      },
    ],
    [t]
  )

  const tenantId = getTenantId()
  const logoUrl = getTenantLogoUrl(tenantId, tenantInfo?.logoUrl)

  return (
    <LearnerChromeProvider setHidden={setChromeHidden}>
      <div className="flex h-dvh min-h-0 flex-col overflow-hidden bg-lc-bg-secondary">
        {/* Top Header */}
        {isChromeHidden ? null : (
          <header className="z-20 flex h-20 shrink-0 items-center justify-between border-b border-lc-v3-gray-300 bg-lc-bg-primary px-4 shadow-lc-v3-header md:px-10">
        <div className="flex h-12 w-[88px] items-center justify-center">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={tenantInfo?.name || 'Quebon'}
              className="max-h-12 w-auto max-w-[88px] object-contain"
              onError={(e) => {
                (e.target as HTMLImageElement).src = '/quebon-logo.svg'
              }}
            />
          ) : (
            <div className="flex size-10 items-center justify-center rounded-lg bg-lc-v3-gray-100 text-lc-text-secondary text-lc-v3-body-2 font-bold">
              {tenantInfo?.name?.charAt(0) || 'Q'}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 md:gap-3">
          <LanguageSwitcher />
          <button
            type="button"
            onClick={() => {
              if (isJourneyLocked) {
                toast.info(t(journeyLockedToastKey))
                return
              }
              setJourneyOpen(true)
            }}
            aria-haspopup="dialog"
            aria-expanded={journeyOpen && !isJourneyLocked}
            aria-disabled={isJourneyLocked}
            className={cn(
              'inline-flex h-10 items-center gap-2 rounded-full border border-lc-v3-gray-300 bg-lc-v3-gray-50 px-3.5 text-lc-v3-body-2 font-bold text-lc-text-secondary hover:bg-lc-v3-gray-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lc-key-500',
              isJourneyLocked && 'text-lc-text-disabled',
            )}
          >
            {isJourneyLocked ? (
              <Lock className="size-[18px]" aria-hidden="true" />
            ) : (
              <Map className="size-[18px]" aria-hidden="true" />
            )}
            <span className="hidden sm:inline">{t('learnerHeader.journey')}</span>
          </button>
          <Link
            to="/my/profile"
            className="inline-flex h-10 items-center gap-2 rounded-full border border-lc-v3-gray-300 bg-lc-v3-gray-50 py-1.5 pl-1.5 pr-3.5 hover:bg-lc-v3-gray-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lc-key-500"
          >
            <UserInitialAvatar
              name={user?.fullName}
              seed={user?.id}
              className="size-8 text-lc-v3-caption-1"
            />
            <span className="hidden text-lc-v3-body-2 font-bold text-lc-text-secondary sm:inline">
              {user?.fullName}
            </span>
          </Link>
          <HeaderLogoutButton />
        </div>
          </header>
        )}

        {/* Main Content */}
        <main ref={contentRef} className="min-h-0 flex-1 overflow-y-auto bg-lc-bg-secondary">
          {isTrialRestricted && (
            <div className="mx-auto mt-3 max-w-[960px] px-4 md:px-10">
              <div role="status" className="rounded-2xl bg-lc-success-50 px-4 py-3 text-lc-v3-body-2 font-semibold text-lc-text-primary">
                {attendance?.trialStatus === 'PENDING'
                  ? t('trial.pending', { set: attendance.trialSetCode })
                  : t('trial.active', {
                      set: attendance?.trialSetCode,
                      days: attendance?.trialRemainingDays ?? 0,
                      end: attendance?.trialPeriodEnd,
                    })}
              </div>
            </div>
          )}
          <Outlet />
        </main>

        {/* Bottom Tab Bar */}
        {isChromeHidden ? null : (
          <nav
            className="z-20 h-[72px] shrink-0 border-t border-lc-v3-gray-300 bg-lc-bg-primary"
            style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
          >
        <div className="mx-auto flex h-full max-w-[640px] items-center justify-between px-2 sm:px-4">
          {tabs.map(({ to, icon: Icon, label }) => {
            const isAttendanceLocked = isAttendanceRestricted && to === '/my/today'
            const isPreviewLocked = isPreview && lockedTabs.includes(to)
            const isTrialLocked =
              isTrialRestricted && ['/my/library', '/my/playground'].includes(to)
            if (isAttendanceLocked || isPreviewLocked || isTrialLocked) {
              const lockedToastKey = isAttendanceLocked
                ? 'attendance.todayLockedToast'
                : isTrialLocked
                  ? 'trial.lockedToast'
                  : 'preview.lockedToast'
              return (
                <button
                  key={to}
                  type="button"
                  onClick={() => toast.info(t(lockedToastKey))}
                  className="relative flex items-center justify-center gap-2 rounded-2xl px-3 py-3.5 text-lc-text-disabled sm:px-4"
                >
                  <Lock className="size-5" />
                  <span className="text-lc-v3-body-2 font-semibold">{label}</span>
                </button>
              )
            }
            return (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                cn(
                  'relative flex items-center justify-center gap-2 rounded-2xl px-3 py-3.5 transition-colors sm:px-4',
                  isActive
                    ? 'bg-lc-bg-key-light text-lc-key-600'
                    : 'text-lc-v3-gray-800 hover:bg-lc-v3-gray-50',
                )
              }
            >
              {({ isActive }) => (
                <>
                  <Icon
                    className={cn('size-5', isActive && 'stroke-[2.5]')}
                  />
                  <span
                    className="text-lc-v3-body-2 font-semibold"
                  >
                    {label}
                  </span>
                </>
              )}
            </NavLink>
            )
          })}
        </div>
          </nav>
        )}
        <CourseJourneyDialog
          open={journeyOpen && !isJourneyLocked}
          onClose={() => setJourneyOpen(false)}
        />
      </div>
    </LearnerChromeProvider>
  )
}
